import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logos } from '../assets';
import { getPublicFooter } from '../api/api';
import './Footer.css';

// ── Icônes réseaux sociaux ─────────────────────────────────────────────
const SocialIcon = ({ name }) => {
  switch (name?.toLowerCase()) {
    case 'linkedin':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>;
    case 'facebook':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>;
    case 'twitter':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
    case 'instagram':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>;
    case 'youtube':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon fill="#fff" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" /></svg>;
    default:
      return null;
  }
};

function isExternal(href) {
  return href && (href.startsWith('http') || href.startsWith('//'));
}

function FooterLink({ href, children }) {
  if (!href) return <span>{children}</span>;
  if (isExternal(href)) {
    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
  }
  return <Link to={href}>{children}</Link>;
}

// Rendu du tagline multi-lignes (split sur \n) sans HTML injection
function Tagline({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <p className="footer__tagline">
      {lines.map((line, i) => (
        <span key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

// ── Footer CMS ─────────────────────────────────────────────────────────
function FooterCms({ sections }) {
  const brand = sections.find(s => s.type === 'brand')?.content || {};
  const contact = sections.find(s => s.type === 'contact')?.content || {};
  const social = sections.find(s => s.type === 'social')?.content || {};
  const linkCols = sections.filter(s => s.type === 'links');
  const legal = sections.find(s => s.type === 'legal')?.content || {};

  return (
    <footer className="footer">
      <div className="footer__top">

        {/* BRAND + CONTACT + SOCIAL */}
        <div className="footer__brand">
          <Link to="/"><img src={logos.light} alt={brand.name || 'Tech Talent Accelerator'} className="footer__logo" /></Link>
          <Tagline text={brand.tagline} />
          {(contact.email || contact.phone || contact.address) && (
            <div className="footer__contact">
              {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
              {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>}
              {contact.address && <span>{contact.address}</span>}
            </div>
          )}
          {social.items && social.items.length > 0 && (
            <div className="footer__socials">
              {social.items.map((item, i) => (
                <a
                  key={i}
                  href={item.href || '#'}
                  className="footer__social"
                  aria-label={item.label}
                  target={isExternal(item.href) ? '_blank' : undefined}
                  rel="noopener noreferrer"
                >
                  <SocialIcon name={item.icon} />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* COLONNES LIENS */}
        {linkCols.map((col, i) => {
          const c = col.content;
          return (
            <div key={i} className="footer__col">
              <h4>{c.title}</h4>
              <ul>
                {(c.items || []).map((item, j) => (
                  <li key={j}><FooterLink href={item.href}>{item.label}</FooterLink></li>
                ))}
              </ul>
            </div>
          );
        })}

      </div>

      <div className="footer__bottom">
        <span className="footer__copy">
          {legal.copyright || `© ${new Date().getFullYear()} Tech Talent Accelerator. Tous droits réservés.`}
        </span>
        {legal.powered_label && (
          <span className="footer__powered">
            {legal.powered_by || 'Propulsé par'}{' '}
            <a href={legal.powered_href || '#'} target="_blank" rel="noopener noreferrer">
              {legal.powered_label}
            </a>
          </span>
        )}
      </div>
    </footer>
  );
}

// ── Footer fallback (hardcodé) ──────────────────────────────────────────
function FooterFallback() {
  return (
    <footer className="footer">
      <div className="footer__top">
        <div className="footer__brand">
          <Link to="/"><img src={logos.light} alt="Tech Talent Accelerator" className="footer__logo" /></Link>
          <p className="footer__tagline">Accélérer les carrières tech<br />en Afrique de l'Ouest</p>
          <div className="footer__contact">
            <a href="mailto:contact@techtalentaccelerator.com">contact@techtalentaccelerator.com</a>
            <a href="tel:+2250769551559">+225 07 69 55 15 59</a>
            <span>Abidjan, Côte d'Ivoire</span>
          </div>
          <div className="footer__socials">
            <a href="#" className="footer__social" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
            </a>
            <a href="#" className="footer__social" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
            </a>
            <a href="#" className="footer__social" aria-label="Twitter/X" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a href="#" className="footer__social" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
            </a>
            <a href="#" className="footer__social" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon fill="#fff" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" /></svg>
            </a>
          </div>
        </div>
        <div className="footer__col">
          <h4>À propos</h4>
          <ul>
            <li><Link to="/a-propos">Notre mission</Link></li>
            <li><Link to="/projets">Nos projets</Link></li>
            <li><Link to="/actualites">Actualités</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>
        <div className="footer__col">
          <h4>Carrières</h4>
          <ul>
            <li><Link to="/carrieres">Débouchés par domaine</Link></li>
            <li><Link to="/carrieres#entreprises">Espace entreprises</Link></li>
            <li><Link to="/carrieres#recruter">Recruter nos talents</Link></li>
            <li><Link to="/carrieres#temoignages">Témoignages alumni</Link></li>
          </ul>
        </div>
        <div className="footer__col">
          <h4>Bailleurs</h4>
          <ul>
            <li><Link to="/bailleurs">Devenir partenaire bailleur</Link></li>
            <li><Link to="/projets/forprode-40-ia">FORPRODE 40 IA</Link></li>
            <li><Link to="/projets/atos-digital-academy">ATOS Digital Academy</Link></li>
            <li><Link to="/candidater">Candidater à un programme</Link></li>
          </ul>
        </div>
      </div>
      <div className="footer__bottom">
        <span className="footer__copy">© {new Date().getFullYear()} Tech Talent Accelerator. Tous droits réservés.</span>
        <span className="footer__powered">Propulsé par <a href="https://www.sira-labs.com" target="_blank" rel="noopener noreferrer">Sira-Labs</a></span>
      </div>
    </footer>
  );
}

// ── Export principal ────────────────────────────────────────────────────
export default function Footer() {
  const [sections, setSections] = useState(null);

  useEffect(() => {
    getPublicFooter()
      .then(data => {
        // L'API retourne {"data": [...]}
        const items = data?.data ?? (Array.isArray(data) ? data : null);
        setSections(Array.isArray(items) ? items : null);
      })
      .catch(() => setSections(null));
  }, []);

  if (!sections || sections.length === 0) return <FooterFallback />;
  return <FooterCms sections={sections} />;
}
